import React from "react";

import { ContentLoader } from "@common/components/util/ContentLoader";
import { Loading } from "@common/components/util/Loading";
import { Workbench } from "@common/components/Workbench";
import * as Constants from "@mapping/constants";
import { SettingsComponent, useSettings } from "../Mapping/Settings";
import { Help } from "./Help";
import { MappingWrapper } from "./MappingWrapper";
import { ITAP_BOT_EMAIL, ITAP_USER_EMAIL } from "@common/components/Workbench/types";
import { JobWarning } from "../../../common/api/v1.1/types/jobs";

const loadRoles = [
  "mapping-reviewed-xml",
  "mapping-predicted-xml",
  "mapping-taxonomy-json",
  "zoning-pagehr-png",
  "zoning-pagesmeta-json",
];

type Props = Readonly<{
  review: boolean;
}>;

export const MappingWorkbench = ({ review }: Props) => {
  const [settings, setSettings] = useSettings();
  return (
    <Workbench
      blockingStep={Constants.TASK_KIND}
      docs={<Help review={review} />}
      loadRoles={loadRoles}
      review={review}
      settings={<SettingsComponent currentSettings={settings} updateSettings={setSettings} />}
      workbenchTitle={Constants.APP_TITLE}
    >
      {({ actions: { resolveWarning, checkinJob, saveContent, showMessage }, auth, contents, noteCount, notes, job }) => {
        const contentToLoad = contents.filter(
          c =>
            c.role === "mapping-predicted-xml" ||
            c.role === "mapping-reviewed-xml" ||
            c.role === "mapping-taxonomy-json" ||
            c.role === "zoning-pagehr-png" ||
            c.role === "zoning-pagesmeta-json",
        );
        return (
          <ContentLoader
            allowFailures={["mapping-reviewed-xml", "zoning-pagehr-png", "zoning-pagesmeta-json"]}
            auth={auth.authString}
            content={contentToLoad}
            job={job}
          >
            {results => {
              if (results.ready) {
                return (
                  <MappingWrapper
                    resolveWarning={resolveWarning}
                    contents={results.contents}
                    noteCount={noteCount}
                    notes={notes}
                    review={review}
                    job={job}
                    authToken={auth.authString}
                    taxonomyAdmin={auth.user.taxonomy_admin}
                    showMessage={showMessage}
                    saveContent={async (xml: string) => {
                      const role = "mapping-reviewed-xml";
                      let order = 1;
                      let isNew = true;
                      const reviewedContent = contents.find(c => c.role === role);
                      if (reviewedContent) {
                        order = reviewedContent.order;
                        isNew = false;
                      }
                      await saveContent(
                        role,
                        order,
                        xml,
                        isNew,
                        "application/xml",
                        undefined,
                        true,
                      );
                    }}
                    settings={settings}
                    submitJob={async () => {
                      await checkinJob();
                    }}
                    acceptAllOnSave={auth.user.email === ITAP_USER_EMAIL || auth.user.email.endsWith(ITAP_BOT_EMAIL)}
                  />
                );
              }
              if (results.failed) {
                const error = new Error(
                  "Failed to load job. Please report this error to a system administrator.",
                );
                return <Loading error={error} text={"Job failed to load."} />;
              }
              return <Loading progress={results.progress} text={"Fetching job contents."} />;
            }}
          </ContentLoader>
        );
      }}
    </Workbench>
  );
};
MappingWorkbench.displayName = "MappingWorkbench";
